import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import AuthLayout from './components/AuthLayout';
import { useRegister } from './api/auth';
import { ValidationErrors } from './types';

export default function Register() {
  const navigate = useNavigate();
  const { mutateAsync: register, isPending } = useRegister();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState<ValidationErrors>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    try {
      await register(formData);
      navigate('/dashboard'); 
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string; errors: ValidationErrors }>;
      
      if (axiosError.response?.status === 422) {
        setErrors(axiosError.response.data.errors || {});
        toast.error(axiosError.response.data.message || 'Please check the form for errors.');
      }
    }
  };

  return (
    <AuthLayout 
      title="Create an account" 
      subtitle="Start finding verified leads in minutes."
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <Input 
          label="Full Name" 
          type="text" 
          placeholder="John Doe" 
          required 
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          error={errors.name?.[0]}
        />

        <Input 
          label="Work Email" 
          type="email" 
          placeholder="you@company.com" 
          required 
          value={formData.email}
          onChange={(e) => setFormData({...formData, email: e.target.value})}
          error={errors.email?.[0]}
        />
        
        <Input 
          label="Password" 
          type="password" 
          placeholder="Create a strong password" 
          required 
          value={formData.password}
          onChange={(e) => setFormData({...formData, password: e.target.value})}
          error={errors.password?.[0]}
        />

        <div className="text-xs text-slate-500 leading-relaxed">
          By signing up, you agree to our{' '}
          <a href="#" className="font-medium text-brand-700 hover:text-brand-900 transition-colors">Terms of Service</a> and{' '}
          <a href="#" className="font-medium text-brand-700 hover:text-brand-900 transition-colors">Privacy Policy</a>.
        </div>

        <Button type="submit" fullWidth isLoading={isPending}>
          Create account
        </Button>
      </form>

      {/* Social Auth Separator */}
      <div className="mt-6">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-slate-500">Or continue with</span>
          </div>
        </div>
        
        <div className="mt-6">
          <Button variant="outline" fullWidth type="button">
            <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" className="w-5 h-5 mr-2" />
            Google
          </Button>
        </div>
      </div>

      <p className="mt-8 text-center text-sm text-slate-600">
        Already have an account?{' '}
        <Link to="/login" className="font-semibold text-brand-700 hover:text-brand-900 transition-colors">
          Log in
        </Link>
      </p>
    </AuthLayout>
  );
}